import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Shield, Edit } from 'lucide-react'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { botsApi } from '@/shared/api/bots'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export default function BotAccessAdd() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const { webApp, hapticFeedback, hapticNotification } = useTelegram()
  const queryClient = useQueryClient()

  const [telegramUserId, setTelegramUserId] = useState('')
  const [selectedRole, setSelectedRole] = useState<'admin' | 'editor'>('editor')

  // Auto-manage back button
  useBackButton()

  // Grant access mutation
  const grantAccessMutation = useMutation({
    mutationFn: ({ telegramUserId, role }: { telegramUserId: number; role: 'admin' | 'editor' }) =>
      botsApi.grantBotAccess(botId!, telegramUserId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botAccess', botId] })
      hapticNotification('success')
      webApp?.showPopup({
        title: 'Успешно',
        message: 'Доступ успешно предоставлен',
        buttons: [{ type: 'ok' }],
      }, () => {
        navigate(`/bots/${botId}/access`, { replace: true })
      })
    },
    onError: (error: any) => {
      const message = error.response?.data?.details || error.message || 'Не удалось предоставить доступ'
      hapticNotification('error')
      webApp?.showPopup({
        title: 'Ошибка',
        message,
        buttons: [{ type: 'ok' }],
      })
    },
  })

  const handleGrantAccess = () => {
    const userId = parseInt(telegramUserId, 10)
    if (isNaN(userId) || userId <= 0) {
      hapticNotification('error')
      webApp?.showPopup({
        title: 'Неверный ввод',
        message: 'Пожалуйста, введите корректный Telegram ID',
        buttons: [{ type: 'ok' }],
      })
      return
    }

    grantAccessMutation.mutate({ telegramUserId: userId, role: selectedRole })
  }

  // Setup MainButton
  useEffect(() => {
    if (!webApp) return

    const isValid = telegramUserId.length > 0 && !isNaN(parseInt(telegramUserId, 10))

    webApp.MainButton.setText('Добавить пользователя')
    webApp.MainButton.color = '#5288c1'
    webApp.MainButton.textColor = '#ffffff'
    webApp.MainButton.show()
    webApp.MainButton.onClick(handleGrantAccess)

    if (isValid && !grantAccessMutation.isPending) {
      webApp.MainButton.enable()
    } else {
      webApp.MainButton.disable()
    }

    return () => {
      webApp.MainButton.offClick(handleGrantAccess)
      webApp.MainButton.hide()
    }
  }, [telegramUserId, selectedRole, grantAccessMutation.isPending, webApp])

  // Show progress on MainButton
  useEffect(() => {
    if (!webApp) return

    if (grantAccessMutation.isPending) {
      webApp.MainButton.showProgress()
      webApp.MainButton.disable()
    } else {
      webApp.MainButton.hideProgress()
      if (telegramUserId.length > 0) {
        webApp.MainButton.enable()
      }
    }
  }, [grantAccessMutation.isPending, telegramUserId, webApp])

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header with title */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-tg-text">Добавить пользователя</h1>
          <p className="text-sm text-tg-hint">Предоставить доступ другому пользователю</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Telegram User ID Input */}
        <div className="bg-tg-secondary-bg rounded-2xl p-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-tg-text mb-2 block">Telegram ID пользователя</span>
            <input
              type="number"
              value={telegramUserId}
              onChange={(e) => setTelegramUserId(e.target.value)}
              placeholder="Введите Telegram ID"
              className={`w-full px-4 py-3 bg-tg-secondary-bg border-2 rounded-xl text-tg-text placeholder:text-tg-hint/50 focus:outline-none transition-colors ${
                telegramUserId.length === 0
                  ? 'border-[#5288c1]/30 focus:border-[#5288c1] focus:ring-2 focus:ring-[#5288c1]/20'
                  : 'border-tg-secondary-bg focus:border-[#5288c1] focus:ring-2 focus:ring-[#5288c1]/20'
              }`}
            />
          </label>
          <p className="text-xs text-tg-hint">
            Вы можете узнать Telegram ID с помощью @userinfobot или подобных ботов
          </p>
        </div>

        {/* Role Selection */}
        <div className="bg-tg-secondary-bg rounded-2xl p-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-tg-text mb-2 block">Роль</span>
          </label>

          {/* Role options */}
          <div className="space-y-2">
            <button
              onClick={() => {
                setSelectedRole('editor')
                hapticFeedback('light')
              }}
              className={`w-full p-4 rounded-xl transition-all ${
                selectedRole === 'editor'
                  ? 'bg-tg-button/10 ring-2 ring-tg-button'
                  : 'bg-tg-bg'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${
                  selectedRole === 'editor' ? 'bg-tg-button/20' : 'bg-tg-hint/10'
                }`}>
                  <Edit className={`w-5 h-5 ${
                    selectedRole === 'editor' ? 'text-tg-button' : 'text-tg-hint'
                  }`} />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-tg-text">Редактор</div>
                  <div className="text-sm text-tg-hint mt-0.5">
                    Может изменять настройки бота и управлять ресурсами
                  </div>
                </div>
                {selectedRole === 'editor' && (
                  <div className="w-5 h-5 rounded-full bg-tg-button flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                )}
              </div>
            </button>

            <button
              onClick={() => {
                setSelectedRole('admin')
                hapticFeedback('light')
              }}
              className={`w-full p-4 rounded-xl transition-all ${
                selectedRole === 'admin'
                  ? 'bg-tg-button/10 ring-2 ring-tg-button'
                  : 'bg-tg-bg'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${
                  selectedRole === 'admin' ? 'bg-tg-button/20' : 'bg-tg-hint/10'
                }`}>
                  <Shield className={`w-5 h-5 ${
                    selectedRole === 'admin' ? 'text-tg-button' : 'text-tg-hint'
                  }`} />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-tg-text">Админ</div>
                  <div className="text-sm text-tg-hint mt-0.5">
                    Полный доступ, включая управление другими пользователями (в будущем)
                  </div>
                </div>
                {selectedRole === 'admin' && (
                  <div className="w-5 h-5 rounded-full bg-tg-button flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Info card */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="text-sm text-amber-600 dark:text-amber-500">
            <p className="font-medium mb-1">Примечание:</p>
            <p>Пользователь должен быть зарегистрирован в системе хотя бы один раз.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
